import { useState, useEffect, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom'
import posthog from 'posthog-js'
import { Helmet } from 'react-helmet-async'
import { setSessionFromTokens, consumePostAuthRedirect } from './services/auth'
import { useSpotlight } from './hooks/useSpotlight'
import { useAuth } from './hooks/useAuth'
import { usePremium } from './hooks/usePremium'
import { useTheme } from './hooks/useTheme'
import LockIcon from './components/LockIcon'
import ErrorBoundary from './components/ErrorBoundary'
import { PdfExportProvider } from './hooks/usePdfExport'
import { PAUSE_SINGLE_REPORT, PAUSE_SUBSCRIPTIONS } from './config'
import { buildSidestreetCanonical } from './utils/canonicalUrl'
import { OPEN_AUTH_MODAL_EVENT } from './utils/requestAuthModal'
import { useClaimPendingPurchase } from './hooks/useClaimPendingPurchase'
import PdfToast from './components/PdfToast'

// LandingPage is the most common entry point
import LandingPage from './pages/LandingPage'

// All other pages are lazy-loaded: each becomes its own JS chunk that only downloads
// when the user first navigates to that route.
const BrowsePredictions  = lazy(() => import('./pages/BrowsePredictions'))
const CityStats          = lazy(() => import('./pages/CityStats'))
const CityStatsDetail    = lazy(() => import('./pages/CityStatsDetail'))
const NeighbourhoodStats = lazy(() => import('./pages/NeighbourhoodStats'))
const PropertyDetails    = lazy(() => import('./pages/PropertyDetails'))
const CustomPrediction   = lazy(() => import('./pages/CustomPrediction'))
const SavedPredictions   = lazy(() => import('./pages/SavedPredictions'))
const UnlockedReports    = lazy(() => import('./pages/UnlockedReports'))
const MyHome             = lazy(() => import('./pages/MyHome'))
const MyReports          = lazy(() => import('./pages/MyReports'))
const MyPurchases        = lazy(() => import('./pages/MyPurchases'))
const ROICalculator      = lazy(() => import('./pages/ROICalculator'))
const Pricing            = lazy(() => import('./pages/Pricing'))
const PrintReport        = lazy(() => import('./pages/PrintReport'))
const PrivacyPolicy      = lazy(() => import('./pages/PrivacyPolicy'))
const TermsOfService     = lazy(() => import('./pages/TermsOfService'))
const DataUseCompliance  = lazy(() => import('./pages/DataUseCompliance'))
const AuthCallback       = lazy(() => import('./pages/AuthCallback'))
const ResetCallback      = lazy(() => import('./pages/ResetCallback'))
const ResetPassword      = lazy(() => import('./pages/ResetPassword'))
const ProStats           = lazy(() => import('./pages/ProStats'))
const Locations          = lazy(() => import('./pages/Locations'))
const SampleReport       = lazy(() => import('./pages/SampleReport'))
const RealtorPlus        = lazy(() => import('./pages/RealtorPlus'))

// Modals are lazy too — they're only needed when the user opens them
const AuthModal    = lazy(() => import('./components/AuthModal'))
const ROITeaseModal = lazy(() => import('./components/ROITeaseModal'))

// Minimal spinner used as the Suspense fallback while a page chunk downloads
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
    </div>
  )
}

/**
 * Captures auth tokens from URL hash on load (e.g. Supabase email confirmation redirects to Site URL with #access_token=...).
 * Logs the user in even when they land on / instead of /auth/callback.
 * Skips on /auth/callback so AuthCallback can read the hash and show success (avoids race + brief "no authentication" message).
 */
function CaptureHashTokens() {
  const location = useLocation()
  const navigate = useNavigate()
  useEffect(() => {
    const normalizedPath = location.pathname.replace(/\/+$/, '') || '/'
    if (normalizedPath === '/auth/callback' || normalizedPath === '/auth/reset-callback') return
    const hash = window.location.hash
    if (!hash) return
    const params = new URLSearchParams(hash.substring(1))
    const accessToken = params.get('access_token')
    const refreshToken = params.get('refresh_token')
    if (accessToken) {
      setSessionFromTokens(accessToken, refreshToken, null)
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
      // If Supabase Site URL is "/" (not /auth/callback), tokens land here — still honor
      // post_auth_redirect so quick-predict guests return to /predict after Google.
      const target = consumePostAuthRedirect()
      const here = `${location.pathname}${location.search || ''}`
      if (target && target !== here) {
        navigate(target, { replace: true })
      }
    }
  }, [location.pathname, location.search, navigate])
  return null
}

/**
 * Ensures each route starts at the top of the page.
 * Without this, SPA navigation can preserve previous scroll position.
 * Also captures PostHog $pageview on each route change for SPA tracking.
 */
function ScrollToTopOnRouteChange() {
  const { pathname, search, hash } = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
    const normalizedPath = pathname.replace(/\/+$/, '') || '/'
    const routeType = normalizedPath === '/' ? 'landing' : normalizedPath === '/predict' ? 'predict' : 'other'
    posthog.capture('$pageview', {
      pathname,
      search,
      hash,
      route_type: routeType,
      page_url: `${window.location.origin}${pathname}${search}${hash}`,
    })
  }, [pathname, search, hash])

  return null
}

/**
 * ROICalculator route wrapper.
 * State reset on navigation is handled inside ROICalculator via useEffect([location.key]).
 * Removing key={location.key} prevents a full remount, so React Query cache is preserved
 * and cached benchmarks / scenarios / saved predictions are served instantly.
 */
function ROICalculatorRoute() {
  return <ROICalculator />
}

function RouteSeo() {
  const { pathname } = useLocation()
  const canonicalUrl = buildSidestreetCanonical(pathname)
  const p = pathname.replace(/\/+$/, '') || '/'

  let title = 'Side Street | Canadian Real Estate AI Predictions'
  let description = 'AI-powered Canadian real estate predictions, market insights, and city stats across 50+ markets.'

  if (p === '/') {
    title = 'Side Street | Canadian Real Estate AI Predictions'
    description = 'Explore AI-powered predictions across 50+ Canadian real estate markets. Browse city stats, market trends, and valuation insights.'
  } else if (p === '/browse') {
    title = 'Browse Predictions | Side Street'
    description = 'Browse live Canadian property predictions with confidence ranges, market pricing signals, and neighborhood context.'
  } else if (p === '/citystats') {
    title = 'City Real Estate Stats | Side Street'
    description = 'Compare city-level real estate market statistics, model accuracy, and pricing trends across Canadian markets.'
  } else if (p.startsWith('/citystats/')) {
    const cityName = decodeURIComponent(p.replace('/citystats/', ''))
    title = `${cityName} Real Estate Stats & AI Predictions | Side Street`
    description = `Explore ${cityName} real estate market stats, neighborhood trends, and AI-powered price intelligence updated weekly.`
  } else if (p.startsWith('/neighbourhood/')) {
    const parts = p.replace('/neighbourhood/', '').split('/')
    const cityDisplay = parts[0].replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    const nbDisplay = (parts[1] || '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    title = `${nbDisplay} Real Estate Stats — ${cityDisplay} | Side Street`
    description = `AI-powered real estate stats for ${nbDisplay}, ${cityDisplay}. Median prices, year-over-year trends, and market signals updated weekly.`
  } else if (p === '/pricing') {
    title = 'Pricing | Side Street'
    description = 'Choose the Side Street plan that fits your workflow, from free market exploration to premium forecasting tools.'
  } else if (p === '/predict') {
    title = 'Custom Property Prediction | Side Street'
    description = 'Generate an AI-powered home valuation using your property details and local market context.'
  } else if (p === '/roi-calculator') {
    title = 'Real Estate ROI Calculator | Side Street'
    description = 'Estimate renovation and investment ROI with data-driven assumptions tailored to Canadian housing markets.'
  } else if (p === '/locations') {
    title = 'Canadian Real Estate Markets | Side Street'
    description = 'Explore AI-powered housing predictions and market stats for 60+ Canadian cities. Browse by province to find your market.'
  } else if (p === '/sample-report') {
    title = 'Sample AI Property Report | Side Street'
    description = 'Preview a full AI-powered property analysis report with confidence ranges, adjusted comps, and neighbourhood insights.'
  } else if (p === '/realtor-plus') {
    title = 'Realtor+ Local Leads | Side Street'
    description = 'Join Realtor+ to receive qualified local buyer and seller leads in your market.'
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Side Street" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
    </Helmet>
  )
}

/**
 * Navigation component
 */
function Navigation() {
  const location = useLocation()
  const { user, isAuthenticated, signOut, loading: authLoading } = useAuth()
  const { tierDisplayName, isPremium, isHomeownerWealth, canUseROI, loading: premiumLoading } = usePremium()
  const { resetAll: resetSpotlightTips } = useSpotlight()
  const loading = authLoading || premiumLoading
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const [authModalMode, setAuthModalMode] = useState('signin')
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [roiTeaseOpen, setRoiTeaseOpen] = useState(false)
  const [betaInfoOpen, setBetaInfoOpen] = useState(false)
  const { theme, toggle: toggleTheme } = useTheme()
  const showNavProBadges = !loading && !(isPremium || isHomeownerWealth)

  const isActive = (path) => {
    const p = location.pathname.replace(/\/+$/, '') || '/'
    const t = path.replace(/\/+$/, '') || '/'
    return p === t
  }

  const handleSignOut = async () => {
    await signOut()
    setUserMenuOpen(false)
    setMobileMenuOpen(false)
  }

  const handleManageSubscription = async () => {
    setUserMenuOpen(false)
    try {
      const { getAccessToken } = await import('./services/auth')
      const token = getAccessToken()
      if (!token) { window.location.href = '/pricing'; return }
      const { createPortalSession } = await import('./services/stripe')
      const { url, error } = await createPortalSession(token)
      if (url) {
        window.location.href = url
      } else {
        // No Stripe customer (free user) — send to pricing page
        console.warn('Portal error:', error?.message)
        window.location.href = '/pricing'
      }
    } catch (err) {
      console.error('Manage subscription error:', err)
      window.location.href = '/pricing'
    }
  }

  const openAuthModal = (mode = 'signin') => {
    setAuthModalMode(mode)
    setAuthModalOpen(true)
    setMobileMenuOpen(false)
  }

  useEffect(() => {
    const onOpen = (e) => {
      const mode = e.detail?.mode === 'signin' ? 'signin' : 'signup'
      setAuthModalMode(mode)
      setAuthModalOpen(true)
      setMobileMenuOpen(false)
    }
    window.addEventListener(OPEN_AUTH_MODAL_EVENT, onOpen)
    return () => window.removeEventListener(OPEN_AUTH_MODAL_EVENT, onOpen)
  }, [])

  return (
    <>
      <header className="sticky top-0 z-50 w-full h-[92px] sm:h-[100px] border-b border-solid border-[#e7ecf3] dark:border-slate-800 bg-white/80 dark:bg-background-dark/80 backdrop-blur-md px-4 sm:px-6 md:px-20 py-2 overflow-visible">
        <div className="relative max-w-[1200px] mx-auto h-full flex items-center justify-between whitespace-nowrap">
          <Link to="/" className="relative flex items-center gap-2 sm:gap-3 text-primary h-full" onClick={() => setMobileMenuOpen(false)}>
            <div className="h-[64px] sm:h-[72px] w-[160px] sm:w-[180px] xl:w-[200px] shrink-0 flex items-center -ml-4 sm:-ml-6 md:-ml-8">
              <img
                src="/images/new_logo_2.svg"
                alt="Side Street logo"
                width={400}
                height={200}
                className="h-full w-full object-contain object-left object-top"
              />
            </div>
          </Link>
          <div className={`absolute left-1/2 top-1/2 -translate-y-1/2 sm:hidden ${isAuthenticated ? '-translate-x-[88%]' : '-translate-x-[124%]'}`}>
            <button
              type="button"
              onClick={() => setBetaInfoOpen(v => !v)}
              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-extrabold tracking-wide bg-gradient-to-r from-primary/20 to-accent-gold/20 text-primary border border-primary/30 whitespace-nowrap"
              aria-label="Beta information"
              aria-expanded={betaInfoOpen}
              aria-haspopup="dialog"
            >
              BETA
            </button>
          </div>
          {betaInfoOpen && (
            <div
              role="dialog"
              className="fixed sm:hidden top-[88px] left-1/2 -translate-x-1/2 w-[calc(100vw-1.5rem)] max-w-sm px-4 py-4 min-h-[124px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg z-50 whitespace-normal text-center flex items-center justify-center"
            >
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed whitespace-normal break-words">
                Beta launch: the experience is improving weekly.
                <br />
                See a bug? Use the orange feedback button to report it.
              </p>
            </div>
          )}
          <div className="hidden lg:block relative mr-3">
            <button
              type="button"
              onClick={() => setBetaInfoOpen(v => !v)}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold tracking-wide bg-gradient-to-r from-primary/20 to-accent-gold/20 text-primary border border-primary/30 whitespace-nowrap"
              aria-label="Beta information"
              aria-expanded={betaInfoOpen}
              aria-haspopup="dialog"
            >
              BETA
            </button>
            {betaInfoOpen && (
              <div
                role="dialog"
                className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-80 p-4 min-h-[112px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg z-50 whitespace-normal text-center flex items-center justify-center"
              >
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed whitespace-normal break-words">
                  Beta launch: the experience is improving weekly.
                  <br />
                  See a bug? Use the orange feedback button to report it.
                </p>
              </div>
            )}
          </div>
          <div className="hidden lg:flex flex-1 justify-center items-center lg:gap-1.5 xl:gap-5 px-4">
            <Link
              to="/browse/"
              className={`text-xs xl:text-sm font-semibold transition-colors whitespace-nowrap ${
                isActive('/browse')
                  ? 'text-primary'
                  : 'text-navy-deep dark:text-slate-300 hover:text-primary'
              }`}
            >
              Browse
            </Link>
            <Link
              to="/citystats/"
              className={`text-xs xl:text-sm font-semibold transition-colors whitespace-nowrap ${
                isActive('/citystats')
                  ? 'text-primary'
                  : 'text-navy-deep dark:text-slate-300 hover:text-primary'
              }`}
            >
              City Stats
            </Link>
            <Link
              to="/locations/"
              className={`text-xs xl:text-sm font-semibold transition-colors whitespace-nowrap ${
                isActive('/locations')
                  ? 'text-primary'
                  : 'text-navy-deep dark:text-slate-300 hover:text-primary'
              }`}
            >
              Markets
            </Link>
            <div className="relative group">
              {canUseROI ? (
                <Link
                  to="/roi-calculator/"
                  className={`text-xs xl:text-sm font-semibold transition-colors flex items-center gap-1 whitespace-nowrap ${
                    isActive('/roi-calculator')
                      ? 'text-primary'
                      : 'text-navy-deep dark:text-slate-300 hover:text-primary'
                  }`}
                >
                  ROI
                </Link>
              ) : (
                <button
                  onClick={() => setRoiTeaseOpen(true)}
                  className="text-xs xl:text-sm font-semibold text-slate-400 hover:text-primary transition-colors flex items-center gap-1.5 whitespace-nowrap"
                >
                  ROI
                  <LockIcon size="sm" className="text-slate-300" />
                </button>
              )}
            </div>
            <Link
              to="/predict/"
              className={`text-xs xl:text-sm font-semibold transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                isActive('/predict')
                  ? 'text-primary'
                  : 'text-navy-deep dark:text-slate-300 hover:text-primary'
              }`}
            >
              Predict
              {showNavProBadges && (
                PAUSE_SINGLE_REPORT ? (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-700">
                    FREE
                  </span>
                ) : (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gradient-to-r from-primary/20 to-accent-gold/20 text-primary border border-primary/30">
                    PRO
                  </span>
                )
              )}
            </Link>
            {!isAuthenticated && !PAUSE_SUBSCRIPTIONS && (
              <Link
                to="/pricing/"
                className={`text-xs xl:text-sm font-semibold transition-colors whitespace-nowrap ${
                  isActive('/pricing')
                    ? 'text-primary'
                    : 'text-navy-deep dark:text-slate-300 hover:text-primary'
                }`}
              >
                Pricing
              </Link>
            )}
            <Link
              to="/pro-stats/"
              className={`text-xs xl:text-sm font-semibold transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                isActive('/pro-stats')
                  ? 'text-primary'
                  : 'text-navy-deep dark:text-slate-300 hover:text-primary'
              }`}
            >
              Pro Stats
              {showNavProBadges && (
                PAUSE_SUBSCRIPTIONS ? (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-700">
                    FREE
                  </span>
                ) : (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gradient-to-r from-primary/20 to-accent-gold/20 text-primary border border-primary/30">
                    PRO
                  </span>
                )
              )}
            </Link>
          </div>
          <div className="flex gap-2 sm:gap-3 items-center shrink-0 lg:ml-4">
            {isAuthenticated ? (
              <>
                {(isPremium || isHomeownerWealth) && (
                  <span className={`hidden sm:inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-bold ${
                    isPremium ? 'bg-primary/10 text-primary' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                  }`}>
                    {tierDisplayName}
                  </span>
                )}
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-navy-deep dark:text-white text-sm font-semibold hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
                  >
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-xs">
                      {user?.email?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <span className="hidden sm:inline">{user?.email?.split('@')[0] || 'User'}</span>
                    <svg
                      className={`w-4 h-4 transition-transform ${userMenuOpen ? 'rotate-180' : ''} hidden sm:block`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 sm:w-64 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-2 z-50">
                      <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-700">
                        <p className="text-sm font-semibold text-navy-deep dark:text-white truncate">{user?.email}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{tierDisplayName} tier</p>
                      </div>
                      <Link
                        to="/my-home/"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-navy-deep dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                      >
                        <span className="material-symbols-outlined text-base">home</span>
                        <span className="truncate">My Home</span>
                        <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 shrink-0">FREE</span>
                      </Link>
                      {(!PAUSE_SINGLE_REPORT || isPremium) && (
                        <Link
                          to="/my-purchases"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-navy-deep dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                          <span className="material-symbols-outlined text-base">description</span>
                          <span className="truncate">My Reports</span>
                        </Link>
                      )}
                      <Link
                        to="/roi-calculator/"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-navy-deep dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                      >
                        <span className="material-symbols-outlined text-base">calculate</span>
                        <span className="truncate">ROI Calculator</span>
                        {!loading && !canUseROI && (
                          <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-gradient-to-r from-primary/20 to-accent-gold/20 text-primary border border-primary/30 shrink-0">UPGRADE</span>
                        )}
                      </Link>
                      {isPremium && (
                        <Link
                          to="/browse/"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-navy-deep dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                          <span className="material-symbols-outlined text-base">manage_search</span>
                          Saved Searches
                        </Link>
                      )}
                      {!loading && (isPremium ? (
                        <button
                          onClick={handleManageSubscription}
                          className="w-full text-left block px-4 py-2 text-sm text-navy-deep dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                          Manage Subscription
                        </button>
                      ) : !PAUSE_SUBSCRIPTIONS && (
                        <Link
                          to="/pricing/"
                          onClick={() => setUserMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-navy-deep dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                          Upgrade Plan
                        </Link>
                      ))}
                      <button
                        onClick={() => { resetSpotlightTips(); setUserMenuOpen(false) }}
                        className="w-full text-left flex items-center gap-2 px-4 py-2 text-sm text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                      >
                        <span className="material-symbols-outlined text-base">lightbulb</span>
                        Reset Tips
                      </button>
                      <button
                        onClick={handleSignOut}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                      >
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => openAuthModal('signup')}
                  className="hidden sm:flex min-w-[100px] cursor-pointer items-center justify-center rounded-lg h-10 px-4 sm:px-5 bg-primary text-white text-sm font-bold tracking-wide shadow-lg shadow-primary/20 hover:bg-primary/90 transition-all"
                >
                  Get Started
                </button>
                <button
                  onClick={() => openAuthModal('signin')}
                  className="flex min-w-[44px] sm:min-w-[84px] cursor-pointer items-center justify-center rounded-lg h-10 px-2 sm:px-4 bg-slate-100 dark:bg-slate-800 text-navy-deep dark:text-white text-sm font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
                >
                  <span className="hidden sm:inline">Login</span>
                  <span className="sm:hidden material-symbols-outlined text-lg">account_circle</span>
                </button>
              </>
            )}
            {/* Dark mode toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-navy-deep dark:text-white"
              aria-label="Toggle dark mode"
            >
              <span className="material-symbols-outlined text-xl">
                {theme === 'dark' ? 'light_mode' : 'dark_mode'}
              </span>
            </button>
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label="Toggle menu"
            >
              <svg
                className={`w-6 h-6 text-navy-deep dark:text-white transition-transform ${mobileMenuOpen ? 'rotate-90' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
            <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-4 space-y-3">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`block py-2 text-base font-semibold transition-colors ${
                  isActive('/')
                    ? 'text-primary'
                    : 'text-navy-deep dark:text-slate-300 hover:text-primary'
                }`}
              >
                Home
              </Link>
              <Link
                to="/browse/"
                onClick={() => setMobileMenuOpen(false)}
                className={`block py-2 text-base font-semibold transition-colors ${
                  isActive('/browse')
                    ? 'text-primary'
                    : 'text-navy-deep dark:text-slate-300 hover:text-primary'
                }`}
              >
                Browse Predictions
              </Link>
              <Link
                to="/citystats/"
                onClick={() => setMobileMenuOpen(false)}
                className={`block py-2 text-base font-semibold transition-colors ${
                  isActive('/citystats')
                    ? 'text-primary'
                    : 'text-navy-deep dark:text-slate-300 hover:text-primary'
                }`}
              >
                City Stats
              </Link>
              <Link
                to="/locations/"
                onClick={() => setMobileMenuOpen(false)}
                className={`block py-2 text-base font-semibold transition-colors ${
                  isActive('/locations')
                    ? 'text-primary'
                    : 'text-navy-deep dark:text-slate-300 hover:text-primary'
                }`}
              >
                Markets
              </Link>
              <div className="py-2">
                {canUseROI ? (
                  <Link
                    to="/roi-calculator/"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-2 text-base font-semibold transition-colors ${
                      isActive('/roi-calculator')
                        ? 'text-primary'
                        : 'text-navy-deep dark:text-slate-300 hover:text-primary'
                    }`}
                  >
                    <span className="material-symbols-outlined text-xl">calculate</span>
                    ROI Calculator
                  </Link>
                ) : (
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false)
                      setRoiTeaseOpen(true)
                    }}
                    className="flex items-center gap-2 text-base font-semibold text-slate-400 hover:text-primary transition-colors w-full text-left"
                  >
                    <span className="material-symbols-outlined text-xl">calculate</span>
                    ROI Calculator
                    <LockIcon size="sm" className="text-slate-300 ml-auto" />
                  </button>
                )}
              </div>
              <Link
                to="/predict/"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-2 py-2 text-sm font-semibold transition-colors ${
                  isActive('/predict')
                    ? 'text-primary'
                    : 'text-navy-deep dark:text-slate-300 hover:text-primary'
                }`}
              >
                Get Prediction
                {PAUSE_SINGLE_REPORT ? (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-700">
                    FREE
                  </span>
                ) : (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-gradient-to-r from-primary/20 to-accent-gold/20 text-primary border border-primary/30">
                    PRO
                  </span>
                )}
              </Link>
              {!PAUSE_SUBSCRIPTIONS && (
                <Link
                  to="/pricing/"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-2 text-base font-semibold transition-colors ${
                    isActive('/pricing')
                      ? 'text-primary'
                      : 'text-navy-deep dark:text-slate-300 hover:text-primary'
                  }`}
                >
                  Pricing
                </Link>
              )}
              <Link
                to="/pro-stats/"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-2 py-2 text-base font-semibold transition-colors ${
                  isActive('/pro-stats')
                    ? 'text-primary'
                    : 'text-navy-deep dark:text-slate-300 hover:text-primary'
                }`}
              >
                Pro Stats
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gradient-to-r from-primary to-accent-gold text-white shadow-sm">
                  NEW
                </span>
              </Link>
              {isAuthenticated && (
                <Link
                  to="/my-home/"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-2 py-2 text-base font-semibold transition-colors ${
                    isActive('/my-home')
                      ? 'text-primary'
                      : 'text-navy-deep dark:text-slate-300 hover:text-primary'
                  }`}
                >
                  My Home
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">FREE</span>
                </Link>
              )}
              {isAuthenticated && (!PAUSE_SINGLE_REPORT || isPremium) && (
                <Link
                  to="/my-purchases"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-2 py-2 text-base font-semibold transition-colors ${
                    isActive('/my-purchases')
                      ? 'text-primary'
                      : 'text-navy-deep dark:text-slate-300 hover:text-primary'
                  }`}
                >
                  <span className="material-symbols-outlined text-xl">description</span>
                  My Reports
                </Link>
              )}
              {isAuthenticated && (
                <Link
                  to="/roi-calculator/"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-2 py-2 text-sm font-semibold transition-colors ${
                    isActive('/roi-calculator')
                      ? 'text-primary'
                      : 'text-navy-deep dark:text-slate-300 hover:text-primary'
                  }`}
                >
                  ROI Calculator
                  {!loading && !canUseROI && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-gradient-to-r from-primary/20 to-accent-gold/20 text-primary border border-primary/30">UPGRADE</span>
                  )}
                </Link>
              )}
              {!isAuthenticated && (
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false)
                    openAuthModal('signup')
                  }}
                  className="block py-2 text-base font-semibold text-primary text-left w-full"
                >
                  Get Started
                </button>
              )}
            </div>
          </div>
        )}
      </header>
      {authModalOpen && (
        <Suspense fallback={null}>
          <AuthModal
            isOpen={authModalOpen}
            onClose={() => setAuthModalOpen(false)}
            initialMode={authModalMode}
          />
        </Suspense>
      )}
      {roiTeaseOpen && (
        <Suspense fallback={null}>
          <ROITeaseModal
            isOpen={roiTeaseOpen}
            onClose={() => setRoiTeaseOpen(false)}
          />
        </Suspense>
      )}
      {/* Click outside to close user menu and mobile menu */}
      {(userMenuOpen || mobileMenuOpen || betaInfoOpen) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setUserMenuOpen(false)
            setMobileMenuOpen(false)
            setBetaInfoOpen(false)
          }}
        />
      )}
    </>
  )
}

/**
 * Footer component
 * Legal pages are React routes: /privacy-policy, /terms-of-service, /data-use-compliance
 */
function Footer() {
  return (
    <footer className="bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 px-6 md:px-20 py-16">
      <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row md:items-start md:justify-between gap-10">
        <div className="max-w-xs">
          <div className="flex items-center gap-4 text-primary mb-6">
            <div className="h-12 w-48 shrink-0">
              <img
                src="/images/new_logo_2.svg"
                alt="Side Street logo"
                width={400}
                height={200}
                className="w-full h-full object-contain object-center"
              />
            </div>
            {/* <h2 className="text-navy-deep dark:text-white text-lg font-extrabold">Side Street</h2> */}
          </div>
          <p className="text-slate-500 dark:text-slate-400 max-w-xs leading-relaxed text-sm">
            The leading AI-driven real estate forecasting platform for the Canadian residential
            market. Making data accessible to everyone.
          </p>
        </div>
        <div>
          <h4 className="text-navy-deep dark:text-white font-black mb-4">Explore</h4>
          <ul className="flex flex-col gap-3 text-slate-500 dark:text-slate-400 text-sm font-semibold">
            <li>
              <Link to="/browse/" className="hover:text-primary transition-colors">
                Browse Predictions
              </Link>
            </li>
            <li>
              <Link to="/locations/" className="hover:text-primary transition-colors">
                All Markets
              </Link>
            </li>
            <li>
              <Link to="/citystats/" className="hover:text-primary transition-colors">
                City Stats
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="text-navy-deep dark:text-white font-black mb-4">Legal</h4>
          <ul className="flex flex-col gap-3 text-slate-500 dark:text-slate-400 text-sm font-semibold">
            <li>
              <Link to="/privacy-policy/" className="hover:text-primary transition-colors">
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link to="/terms-of-service/" className="hover:text-primary transition-colors">
                Terms of Service
              </Link>
            </li>
            <li>
              <Link to="/data-use-compliance/" className="hover:text-primary transition-colors">
                Data Use Compliance
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="text-navy-deep dark:text-white font-black mb-4">Support</h4>
          <ul className="flex flex-col gap-3 text-slate-500 dark:text-slate-400 text-sm font-semibold">
            <li>
              <a
                href="https://sidestreet.featurebase.app/help"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors"
              >
                Help Center
              </a>
            </li>
            <li>
              <a
                href="https://sidestreet.featurebase.app"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors"
              >
                Feature Requests
              </a>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="text-navy-deep dark:text-white font-black mb-4">Contact</h4>
          <ul className="flex flex-col gap-3 text-slate-500 dark:text-slate-400 text-sm font-semibold">
            <li>
              <a
                href="https://tally.so/r/zxYA98"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors"
              >
                Contact Us
              </a>
            </li>
            <li>
              <Link
                to="/realtor-plus/"
                className="hover:text-primary transition-colors inline-flex items-center gap-1.5"
              >
                <span className="material-symbols-outlined text-sm">handshake</span>
                Realtors: Get Local Leads
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="max-w-[1200px] mx-auto border-t border-slate-200 dark:border-slate-800 mt-12 sm:mt-16 pt-6 sm:pt-8">
        <p className="text-slate-500 text-xs font-bold text-center sm:text-left">
          © 2026 SideStreet. All rights reserved.
        </p>
      </div>
    </footer>
  )
}

/**
 * Main App component with routing and layout
 */
function AppContent() {
  const location = useLocation()
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/'
  const isPrintRoute = normalizedPath === '/report/print'
  useClaimPendingPurchase()

  if (isPrintRoute) {
    return (
      <ErrorBoundary>
        <RouteSeo />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-white text-slate-500">Loading report...</div>}>
          <PrintReport />
        </Suspense>
      </ErrorBoundary>
    )
  }

  return (
    <>
      <CaptureHashTokens />
      <ScrollToTopOnRouteChange />
      <RouteSeo />
      <div className="min-h-screen bg-background-light dark:bg-background-dark text-navy-deep dark:text-slate-50 transition-colors duration-300">
        <Navigation />
        <main className="flex flex-col">
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/browse" element={<BrowsePredictions />} />
                <Route path="/citystats" element={<CityStats />} />
                <Route path="/citystats/:city" element={<CityStatsDetail />} />
                <Route path="/neighbourhood/:city/:neighbourhood" element={<NeighbourhoodStats />} />
                <Route path="/predict" element={<CustomPrediction />} />
                <Route path="/my-purchases" element={<MyPurchases />} />
                <Route path="/saved-predictions" element={<SavedPredictions />} />
                <Route path="/unlocked-reports" element={<Navigate to="/my-purchases" replace />} />
                <Route path="/my-home" element={<MyHome />} />
                <Route path="/my-reports" element={<Navigate to="/my-purchases" replace />} />
                <Route path="/roi-calculator" element={<ROICalculatorRoute />} />
                <Route path="/property/:id?" element={<PropertyDetails />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/data-use-compliance" element={<DataUseCompliance />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/auth/reset-callback" element={<ResetCallback />} />
                <Route path="/auth/reset-password" element={<ResetPassword />} />
                <Route path="/locations" element={<Locations />} />
                <Route path="/sample-report" element={<SampleReport />} />
                <Route path="/pro-stats/:city?" element={<ProStats />} />
                <Route path="/realtor-plus" element={<RealtorPlus />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </main>
        <Footer />
      </div>
    </>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <PdfExportProvider>
          <AppContent />
          <PdfToast />
        </PdfExportProvider>
      </Router>
    </ErrorBoundary>
  )
}

export default App
